import { IS_DEV, IS_PREVIEW } from '../utils/environment';

/**
 * Previewing an ad in situ, on any surface that runs one.
 *
 * The targeting rules (badges, segments, geo, claim state) are not satisfiable
 * on any one account — so there is otherwise no way to look at a real card in
 * its real slot.
 *
 * `?previewAd=<id>` forces that descriptor past the targeting, any
 * route gate and any dismissal. Gated to dev, preview deploys and Diffit
 * admins; inert for everyone else, so a leaked URL shows a real teacher
 * nothing.
 *
 * The param is deliberately ONE name across every surface. A reviewer holds an
 * ad id in their head, not a surface — and the id already says which registry
 * it belongs to, so each surface simply finds nothing for an id that is not
 * its own and says so.
 */
export const PREVIEW_PARAM = 'previewAd';

/**
 * The admin term reads the UNMASKED flag on purpose. Reviewing a card as a
 * teacher means combining this with `?viewAsTeacher=1`, which drops
 * `is_diffit_admin` — so gating the preview on the masked value would have the
 * two switch each other off anywhere IS_DEV and IS_PREVIEW are both false, i.e.
 * exactly on production, which is the one place the combination is needed.
 */
export const canPreview = (isRealDiffitAdmin) => IS_DEV || IS_PREVIEW || !!isRealDiffitAdmin;

// Once per surface+id, not once per render. Now that one param name serves several
// registries, a MISS is the normal case rather than the exceptional one: previewing a
// sidebar id means every mounted export slot is also asked for it and correctly finds
// nothing. Warning on each render buries the one line that matters — the typo — under a
// scroll of expected misses, on the surface being previewed.
const warned = new Set();

/**
 * The forced descriptor for this surface, or null.
 *
 * `hookName` only names the warning's source. A typo'd id would otherwise look
 * exactly like "the ad did not qualify", which is the failure this warning
 * exists to tell apart — and with more than one registry in play, "which
 * surface was even asked" is now part of that answer.
 */
export function resolvePreviewAd({ registry, search, isRealDiffitAdmin, hookName }) {
  if (!canPreview(isRealDiffitAdmin)) return null;

  const previewId = new URLSearchParams(search).get(PREVIEW_PARAM);
  if (!previewId) return null;

  const ad = registry.find((entry) => entry.id === previewId) ?? null;
  if (!ad && !warned.has(`${hookName}:${previewId}`)) {
    warned.add(`${hookName}:${previewId}`);
    console.warn(
      `${hookName}: no ad "${previewId}". Try one of: ${registry.map((a) => a.id).join(', ')}`
    );
  }
  return ad;
}
